import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Custom hook for Yape form validation logic.
 * Handles field validation, error state, and field interactions.
 * @param {Object} initialData - Initial form data
 * @returns {Object} Validation state and handlers
 */
const useYapeValidation = (initialData = {}) => {
  const { t } = useTranslation('payment');
  const [errors, setErrors] = useState({});
  const [fieldInteractions, setFieldInteractions] = useState({
    phoneNumber: Boolean(initialData.phoneNumber),
    otp: Boolean(initialData.otp),
  });

  const validateField = useCallback(
    (fieldName, value) => {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };

        switch (fieldName) {
          case 'phoneNumber':
            if (!value.trim()) {
              newErrors.phoneNumber = t('error.required_field');
            } else if (!/^\d{9}$/.test(value.replace(/\D/g, ''))) {
              newErrors.phoneNumber = t('error.invalid_phone_number');
            } else {
              delete newErrors.phoneNumber;
            }
            break;
          case 'otp':
            if (!value.trim()) {
              newErrors.otp = t('error.required_field');
            } else if (!/^\d{6}$/.test(value.replace(/\D/g, ''))) {
              newErrors.otp = t('error.invalid_otp');
            } else {
              delete newErrors.otp;
            }
            break;
          default:
            break;
        }

        return newErrors;
      });
    },
    [t],
  );

  const markFieldAsInteracted = useCallback((fieldName) => {
    setFieldInteractions((prev) => ({ ...prev, [fieldName]: true }));
  }, []);

  const hasError = useCallback(
    (fieldName) => {
      return !!(fieldInteractions[fieldName] && errors[fieldName]);
    },
    [fieldInteractions, errors],
  );

  const isFormValid = useCallback(() => {
    const hasAnyInteraction = fieldInteractions.phoneNumber || fieldInteractions.otp;
    if (hasAnyInteraction) {
      return !Object.keys(errors).some((key) => errors[key]);
    }
    return false;
  }, [errors, fieldInteractions]);

  return {
    errors,
    fieldInteractions,
    validateField,
    markFieldAsInteracted,
    hasError,
    isFormValid,
  };
};

export default useYapeValidation;
